import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as commissionsService from '../services/commissions.service';
import type { CommissionDTO } from '../types/commissions.types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  network_error: '网络连接失败，请检查网络',
  'invalid commission type': '无效的返佣类型',
  'invalid commission level': '无效的返佣级别',
  'invalid earnings range': '无效的时间范围',
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    return ERROR_MAP[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useCommissionList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const beneficiaryUserId = searchParams.get('beneficiaryUserId') || '';
  const sourceUserId = searchParams.get('sourceUserId') || '';
  const level = searchParams.get('level') || '';
  const type = searchParams.get('type') || '';
  const range = searchParams.get('range') || '';
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;

  const [items, setItems] = useState<CommissionDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (
    p: number,
    buid: string,
    suid: string,
    lv: string,
    tp: string,
    rg: string,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const result = await commissionsService.listCommissions({
        page: p,
        pageSize,
        beneficiaryUserId: buid,
        sourceUserId: suid,
        level: lv,
        type: tp,
        range: rg,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    load(page, beneficiaryUserId, sourceUserId, level, type, range);
  }, [load, page, beneficiaryUserId, sourceUserId, level, type, range]);

  const setBeneficiaryUserId = useCallback((v: string) => {
    updateParams({ beneficiaryUserId: v || null, page: null });
  }, [updateParams]);

  const setSourceUserId = useCallback((v: string) => {
    updateParams({ sourceUserId: v || null, page: null });
  }, [updateParams]);

  const setLevel = useCallback((v: string) => {
    updateParams({ level: v || null, page: null });
  }, [updateParams]);

  const setType = useCallback((v: string) => {
    updateParams({ type: v || null, page: null });
  }, [updateParams]);

  const setRange = useCallback((v: string) => {
    updateParams({ range: v || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const reload = useCallback(() => {
    load(page, beneficiaryUserId, sourceUserId, level, type, range);
  }, [load, page, beneficiaryUserId, sourceUserId, level, type, range]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items, total, page, pageSize, totalPages,
    beneficiaryUserId, sourceUserId, level, type, range,
    loading, error,
    setPage, setBeneficiaryUserId, setSourceUserId, setLevel, setType, setRange,
    resetFilters, reload,
  };
}
